import { defineMessages, FormattedMessage } from 'react-intl';
import { ChevronRight, Redeem } from '@mui/icons-material';
import { Button, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';

import { Strong } from '@/components/rich-text-formatting';
import {
  FEATURE_LAUNCHED_AT,
  INITIAL_SUBS_GIVEN_AWAY,
  SUBS_GIVEN_AWAY_PER_DAY,
} from '@/features/referrals/config';
import { durandalRoutes } from '@/routes/durandal';

const messages = defineMessages({
  GetTwoMonthsFree: {
    defaultMessage: 'Get 4 Months Free',
  },
  SubsGivenAway: {
    defaultMessage:
      'Plooto has given away <Strong>{count, plural, =1 {# subscription} other {# subscriptions}}!</Strong>',
  },
  MakeAReferral: {
    defaultMessage: 'Make a Referral',
  },
});

function getSubsGivenAway() {
  const daysSinceLaunch = differenceInCalendarDays(new Date(), FEATURE_LAUNCHED_AT);
  return (
    INITIAL_SUBS_GIVEN_AWAY + Math.floor(SUBS_GIVEN_AWAY_PER_DAY * Math.max(0, daysSinceLaunch))
  );
}

export type ReferralsCounterWidgetProps = {
  size: 'large' | 'small';
  onNavigateToReferrals: () => void;
};

export const ReferralsCounterWidget = ({
  size,
  onNavigateToReferrals,
}: ReferralsCounterWidgetProps) => (
  <Card
    variant="outlined"
    sx={{
      // We customize these props here to blend in with the existing dashboard sidebar widgets. This
      // should be removed when the dashboard redesign is implemented in favour of the styles
      // applied by variant="outlined".
      border: 'solid 1px #dae1e9',
      boxShadow: '0 1px 1px 0 #dae1e9',
      // These are flavour customizations for this specific component, and should not be removed
      // with the dashboard redesign.
      borderTopColor: 'success.main',
      borderTopWidth: 4,
    }}
  >
    {size === 'small' ? (
      <CardActionArea href={durandalRoutes.user.referrals} onClick={onNavigateToReferrals}>
        <Stack p={2} gap={2} direction="row" alignItems="center">
          <Redeem color="success" />
          <Typography variant="button" component="span" flexGrow="1">
            <FormattedMessage {...messages.GetTwoMonthsFree} />
          </Typography>
          <ChevronRight />
        </Stack>
      </CardActionArea>
    ) : (
      <Stack p={2} gap={1} alignItems="flex-start">
        <Redeem color="success" fontSize="large" />
        <Typography variant="h4" component="span">
          <FormattedMessage {...messages.GetTwoMonthsFree} />
        </Typography>
        <Typography component="span">
          <FormattedMessage
            {...messages.SubsGivenAway}
            values={{ count: getSubsGivenAway(), Strong }}
          />
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="medium"
          // Pull the button into the card padding so that the card text is all aligned.
          sx={{ ml: -1, mb: -1 }}
          href={durandalRoutes.user.referrals}
          onClick={onNavigateToReferrals}
        >
          <FormattedMessage {...messages.MakeAReferral} />
        </Button>
      </Stack>
    )}
  </Card>
);
